import {
  Flame,
  CalendarClock,
  FileDown,
  Ticket,
  Trophy,
  NotebookPen,
  BookOpen,
  Target,
  TrendingUp,
  Sprout,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import GuestPreviewOverlay from "@/components/user-button-pages/GuestPreviewOverlay";
import GuestStudyProgressOverview from "@/app/my/GuestStudyProgressOverview";

/**
 * 비로그인(게스트) /my 진입 시 보여주는 미리보기 화면 (JCG — 게스트 대시보드 프리뷰).
 *
 * 실제 /my 대시보드(page.tsx 로그인 분기)의 레이아웃을 그대로 미러링한 **정적 목업**을 더미
 * 데이터로 채워 흐릿하게 깔고, 그 위에 가운데 로그인 안내 카드를 띄운다.
 * "로그인하면 이런 대시보드가 생긴다"를 미리 보여줘 회원가입 전환을 유도하는 용도.
 *
 * - 흐린 목업 레이어 + 그라데이션 오버레이 + 로그인 카드 구조는 공통 컴포넌트
 *   `GuestPreviewOverlay`(user-button-pages)로 추출했다 — 학습 4개 탭 게스트 프리뷰와 동일 셸.
 * - 훅/상태/데이터 fetch 없는 순수 presentational. 더미 데이터는 모듈 상수.
 * - 색은 전부 디자인 토큰 + amber/계열 light/dark 페어(ui-patterns dark-mode-colors 준수).
 *   error-notes 페이월의 하드코딩 `from-white dark:from-gray-900` 패턴은 따르지 않고 토큰으로.
 */

// ── 더미 데이터 (모두 그럴듯한 한국어 예시값) ────────────────────────────────

/** 최근 7일 출석 스트립: 5일 출석(true). 마지막 칸이 "오늘". */
const MOCK_LAST7: { weekday: string; attended: boolean; isToday: boolean }[] = [
  { weekday: "월", attended: true, isToday: false },
  { weekday: "화", attended: true, isToday: false },
  { weekday: "수", attended: false, isToday: false },
  { weekday: "목", attended: true, isToday: false },
  { weekday: "금", attended: true, isToday: false },
  { weekday: "토", attended: false, isToday: false },
  { weekday: "일", attended: true, isToday: true },
];

/** 도전과제 배지: 획득 2 + 미획득 2 느낌. */
const MOCK_BADGES: { label: string; earned: boolean }[] = [
  { label: "첫 출석", earned: true },
  { label: "7일 연속", earned: true },
  { label: "100문제", earned: false },
  { label: "정답왕", earned: false },
];

/** 일자별 정답률(7일치, 55 → 63% 우상향). */
const MOCK_ACCURACY: number[] = [55, 58, 56, 60, 59, 62, 63];

/** 오늘의 학습 task 목업: 계획 원본 순서 그대로, 5개 중 3개 완료. */
const MOCK_TODAY_TASKS: {
  emoji: string;
  title: string;
  done: boolean;
}[] = [
  { emoji: "⭐️", title: "키", done: true },
  { emoji: "📘", title: "요개논물", done: true },
  { emoji: "📗", title: "반정규화", done: true },
  { emoji: "🔥", title: "정규화", done: false },
  { emoji: "🤔", title: "관계 데이터 모델", done: false },
];

// ── 카드 셸 / 작은 프리미티브 (실제 DashboardCard 시각 골격만 본뜸) ──────────

/** 실제 DashboardCard의 시각 골격을 본뜬 정적 셸(링크/훅 없음). */
function MockCard({
  icon: Icon,
  title,
  linkLabel = "전체 보기",
  premium = false,
  children,
  className,
}: {
  icon?: LucideIcon;
  title?: string;
  linkLabel?: string;
  premium?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  const showHeader = Boolean(title);
  return (
    <section
      className={cn(
        "rounded-xl border border-border bg-card p-4 sm:p-5",
        className,
      )}
    >
      {showHeader && (
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {Icon && (
              <Icon className="h-[18px] w-[18px] shrink-0 text-amber-500" />
            )}
            <span className="truncate text-sm font-bold text-foreground">
              {title}
            </span>
            {premium && (
              <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 dark:bg-amber-950 dark:text-amber-400">
                Premium
              </span>
            )}
          </div>
          <span className="shrink-0 text-xs font-semibold text-muted-foreground">
            {linkLabel}
          </span>
        </div>
      )}
      {children}
    </section>
  );
}

// ── 흐린 목업 대시보드 (실제 레이아웃 1:1 미러링) ────────────────────────────

/** 실제 /my 대시보드 그리드를 더미 데이터로 채운 정적 목업. 전체 aria-hidden. */
function MockDashboard() {
  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 sm:space-y-5">
      {/*
        1행: 연속 학습일 · 시험 D-day · 요약 PDF (JCG-2310 재배치 — 3등분 스탯 카드)
        실제 대시보드(page.tsx)와 동일하게 균등 3등분. 오늘의 학습은 1행에서 빠지고
        바로 아래 풀폭 얇은 바로 미러된다.
         - 모바일: 세로 스택 / sm+: 균등 3등분
      */}
      <div className="grid gap-3 sm:grid-cols-3 lg:gap-4">
        {/* 연속 학습일 */}
        <MockCard icon={Flame} title="연속 학습일" linkLabel="진행상황">
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-extrabold tabular-nums text-amber-600 dark:text-amber-400">
                5
              </span>
              <span className="text-sm text-muted-foreground">일 연속</span>
              <span className="ml-auto rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-600 dark:bg-amber-950 dark:text-amber-400">
                오늘 출석
              </span>
            </div>
            <div className="mt-3 flex justify-between gap-1">
              {MOCK_LAST7.map((d) => (
                <div
                  key={d.weekday}
                  className="flex flex-1 flex-col items-center gap-1"
                >
                  <div
                    className={cn(
                      "h-6 w-full rounded-md",
                      d.attended ? "bg-amber-500" : "bg-muted",
                      d.isToday && "ring-2 ring-amber-300 dark:ring-amber-700",
                    )}
                  />
                  <span className="text-[10px] text-muted-foreground">
                    {d.weekday}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </MockCard>

        {/* 시험 D-day */}
        <MockCard icon={CalendarClock} title="시험 D-day" linkLabel="시험 정보">
          <span className="block text-3xl font-extrabold tabular-nums text-red-500 dark:text-red-400">
            D-41
          </span>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            2026년 정기 기사 2회
          </p>
        </MockCard>

        {/* 요약 PDF */}
        <MockCard icon={FileDown} title="요약 PDF" linkLabel="구매 목록">
          <div className="space-y-2">
            <p className="text-[13px] font-bold text-amber-600 dark:text-amber-400">
              새로 받을 PDF 1개
            </p>
            <ul className="space-y-1">
              <li>
                <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-2.5 py-1.5 text-[12px]">
                  <span className="min-w-0 truncate text-foreground">
                    정처기 실기 요약 PDF
                  </span>
                  <span className="shrink-0 font-bold text-amber-600 dark:text-amber-400">
                    새 버전
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </MockCard>
      </div>

      {/*
        2행: 오늘의 학습 — 풀폭 얇은 가로 바 (JCG-2310)
        실제 위젯(DashboardTodaySchedule)의 얇은 행 폼을 미러:
         [좌] 메타 블록(plan 칩 · Day n/총 · x/y · 얇은 진행 바) + [우] 칩 스트립(단일 행)
      */}
      <MockCard icon={Sprout} title="오늘의 학습" linkLabel="일정 관리">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {/* 좌측 메타 블록 (한 줄, 인라인) */}
          <div className="flex h-7 shrink-0 items-center gap-x-2">
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-bold text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
              이론 4주 완성
            </span>
            <span className="text-[11px] font-semibold tabular-nums text-muted-foreground">
              Day 12/28
            </span>
            <span className="text-[11px] font-bold tabular-nums text-amber-600 dark:text-amber-400">
              3/5
            </span>
            <span className="h-1 w-10 overflow-hidden rounded-full bg-muted">
              <span
                className="block h-full rounded-full bg-blue-500 dark:bg-blue-400"
                style={{ width: "60%" }}
              />
            </span>
          </div>

          {/* 우측 칩(pill) 스트립 — 단일 행 + 가로 스크롤 (실제 위젯 미러) */}
          <div className="min-w-0 flex-1 basis-48">
            <div className="relative">
              <ul className="flex items-center gap-1.5 overflow-x-auto pr-6">
                {MOCK_TODAY_TASKS.map((t) => (
                  <li key={t.title} className="shrink-0">
                    <div
                      className={cn(
                        "flex h-7 items-center gap-1 rounded-full border pl-1 pr-2.5",
                        t.done
                          ? "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50"
                          : "border-border bg-background",
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                          t.done
                            ? "border-amber-500 bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950"
                            : "border-amber-400 bg-background",
                        )}
                      >
                        {t.done ? (
                          <svg
                            viewBox="0 0 14 14"
                            className="h-3 w-3"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M3 7.5 6 10.5 11 4" />
                          </svg>
                        ) : (
                          <span className="h-1.5 w-1.5 rounded-full bg-blue-400 dark:bg-blue-500" />
                        )}
                      </span>
                      <span
                        className={cn(
                          "flex items-center gap-1 whitespace-nowrap text-[12px] leading-none",
                          t.done
                            ? "text-muted-foreground line-through decoration-amber-500/50"
                            : "font-medium text-foreground",
                        )}
                      >
                        <span className="text-[13px] leading-none">
                          {t.emoji}
                        </span>
                        {t.title}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
              <span
                className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-card to-transparent"
                aria-hidden
              />
            </div>
          </div>
        </div>
      </MockCard>

      {/* 3행: 이용권 · 도전과제 · 최근 필기 · 최근 오답노트 (컴팩트 4) */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        {/* 감자 이용권 */}
        <MockCard icon={Ticket} title="감자 이용권" linkLabel="업그레이드">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-lg font-extrabold text-foreground">
              Free
            </span>
            <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
              무료
            </span>
          </div>
          <p className="mt-1 truncate text-[11px] text-muted-foreground">
            업그레이드하면 모든 기능 이용
          </p>
        </MockCard>

        {/* 도전과제 */}
        <MockCard icon={Trophy} title="도전과제">
          <div className="overflow-hidden">
            <div className="flex items-center gap-1.5">
              {MOCK_BADGES.map((b) => (
                <div
                  key={b.label}
                  className={cn(
                    "h-8 w-8 shrink-0 rounded-full border-2",
                    b.earned
                      ? "border-amber-400 bg-amber-100 dark:border-amber-600 dark:bg-amber-950"
                      : "border-dashed border-border bg-muted",
                  )}
                />
              ))}
            </div>
          </div>
        </MockCard>

        {/* 최근 필기 */}
        <MockCard icon={NotebookPen} title="최근 필기">
          <div className="block rounded-lg border border-border bg-background px-3 py-2">
            <span className="block truncate text-[13px] font-semibold text-foreground">
              운영체제 - 프로세스 관리
            </span>
            <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
              운영체제 · 어제
            </span>
          </div>
        </MockCard>

        {/* 최근 오답노트 */}
        <MockCard icon={BookOpen} title="최근 오답노트">
          <div className="block rounded-lg border border-border bg-background px-3 py-2">
            <span className="block truncate font-mono text-[12px] font-semibold text-foreground">
              정규화 3NF / BCNF 차이
            </span>
            <span className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground">
              부분 함수 종속과 이행 함수 종속을 헷갈림. 결정자 기준으로 다시 정리.
            </span>
          </div>
        </MockCard>
      </div>

      {/* 4행: 공부 진행상황 전체현황 ↔ 일자별 정답률 변화 */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* 전체 현황 */}
        <MockCard icon={Target} title="전체 현황" premium>
          <GuestStudyProgressOverview />
        </MockCard>

        {/* 일자별 정답률 변화 (더미 막대 차트) */}
        <MockCard
          icon={TrendingUp}
          title="일자별 정답률 변화"
          linkLabel="오답노트"
          premium
        >
          <div className="flex h-[180px] items-end justify-between gap-2 sm:h-[200px]">
            {MOCK_ACCURACY.map((value, i) => (
              <div
                key={i}
                className="flex flex-1 flex-col items-center justify-end gap-1.5"
              >
                <span className="text-[10px] font-medium text-muted-foreground">
                  {value}%
                </span>
                <div
                  className="w-full rounded-t-md bg-amber-400 dark:bg-amber-500"
                  style={{ height: `${value}%` }}
                />
              </div>
            ))}
          </div>
        </MockCard>
      </div>
    </div>
  );
}

// ── 게스트 미리보기 (흐린 목업 + 오버레이 로그인 카드) ────────────────────────

export default function GuestDashboardPreview() {
  return (
    <GuestPreviewOverlay
      title="로그인하면 내 학습 현황이 한눈에"
      description="연속 학습일·도전과제·최근 학습을 기록하고 한눈에 확인하세요. (공부 진행상황·정답률 분석은 Premium 이용권)"
    >
      <MockDashboard />
    </GuestPreviewOverlay>
  );
}
